import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary from "./components/ErrorBoundary";
import { shadowCSS } from "./utils/constants";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 300000,
    },
  },
});

class Read2EarnPopup extends HTMLElement {
  private shadow: ShadowRoot;
  private reactRoot: ReactDOM.Root | null = null;
  private observer: MutationObserver | null = null;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const styleSheet = new CSSStyleSheet();
    styleSheet.replaceSync(shadowCSS);
    this.shadow.adoptedStyleSheets = [styleSheet];

    const container = document.createElement("div");
    container.className = "App relative w-full";
    this.shadow.appendChild(container);

    // Detect initial theme and observe future changes
    this.observeTheme(container);

    this.reactRoot = ReactDOM.createRoot(container);
    this.reactRoot.render(
      <React.StrictMode>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.reactRoot?.unmount();
  }

  private observeTheme(container: HTMLElement) {
    const setTheme = () => {
      const isDark = document.body.classList.contains("dark");
      container.setAttribute("data-theme", isDark ? "dark" : "light");
    };

    // Set once initially
    setTheme();

    // Watch for theme toggling
    this.observer = new MutationObserver(setTheme);
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }
}

customElements.define("read2earn-popup", Read2EarnPopup);

export default Read2EarnPopup;
